<div class="job-details-dialog py-6 bg-white dark:bg-neutral-900">
  @if (job) {
    <!-- Header -->
    <div class="flex items-center justify-between mb-6 pb-4 border-b border-neutral-200 dark:border-neutral-700">
      <div class="flex items-center gap-4">
        <!-- Job Avatar -->
        <div
          class="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
          [class]="
            job.color ||
            'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20'
          "
        >
          {{ job.avatar || '⚙️' }}
        </div>
        <div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {{ job.name }}
          </h2>
          <div class="flex items-center gap-2">
            <span
              [class]="getStatusColor(job.status)"
              class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
            >
              <i [class]="getStatusIcon(job.status)"></i>
              {{ getStatusLabel(job.status) }}
            </span>
            @if (job.category) {
              <span class="text-sm text-gray-600 dark:text-gray-400">
                {{ job.category }}
              </span>
            }
          </div>
        </div>
      </div>
      <button
        pButton
        type="button"
        icon="pi pi-times"
        class="p-button-text p-button-rounded"
        (click)="onClose()"
        [pTooltip]="'common.actions.close' | transloco"
      ></button>
    </div>

    <!-- Main Content -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left Column -->
      <div class="space-y-6">
        <!-- Description -->
        <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {{ 'jobDetailsDialog.sections.description' | transloco }}
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ job.description || ('jobDetailsDialog.labels.noDescription' | transloco) }}
          </p>
        </div>

        <!-- Basic Information -->
        <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ 'jobDetailsDialog.sections.basicInfo' | transloco }}
          </h3>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.jobId' | transloco }}</span>
              <span class="text-sm text-gray-900 dark:text-white font-mono">{{ job.uuid || ('jobDetailsDialog.labels.notAvailable' | transloco) }}</span>
            </div>
            @if (job.job_type) {
              <div class="flex justify-between items-center">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.jobType' | transloco }}</span>
                <span class="text-sm text-gray-900 dark:text-white">{{ getJobTypeLabel(job.job_type) }}</span>
              </div>
            }
            @if (job.category) {
              <div class="flex justify-between items-center">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.category' | transloco }}</span>
                <span class="text-sm text-gray-900 dark:text-white">{{ job.category }}</span>
              </div>
            }
            @if (job.created_at) {
              <div class="flex justify-between items-center">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.created' | transloco }}</span>
                <span class="text-sm text-gray-900 dark:text-white">{{ job.created_at | date: 'short' }}</span>
              </div>
            }
            @if (job.updated_at) {
              <div class="flex justify-between items-center">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.updated' | transloco }}</span>
                <span class="text-sm text-gray-900 dark:text-white">{{ job.updated_at | date: 'short' }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Reporting Data -->
        <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ 'jobDetailsDialog.sections.reportingData' | transloco }}
          </h3>
          <div class="space-y-3">
            <!-- Rating -->
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.rating' | transloco }}</span>
              <div class="flex items-center gap-2">
                @if (job.rating) {
                  <div class="flex items-center gap-1">
                    @for (i of getFilledStars(job.rating); track i) {
                      <i class="fa-solid fa-star text-yellow-400"></i>
                    }
                    @if (getRatingStars(job.rating).half) {
                      <i class="fa-solid fa-star-half-stroke text-yellow-400"></i>
                    }
                    @for (i of getEmptyStars(job.rating); track i) {
                      <i class="fa-regular fa-star text-gray-300 dark:text-gray-600"></i>
                    }
                  </div>
                  <span class="text-sm font-semibold text-gray-900 dark:text-white">
                    {{ job.rating.toFixed(1) }}
                  </span>
                } @else {
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{ 'jobDetailsDialog.labels.noRating' | transloco }}</span>
                }
              </div>
            </div>
            <!-- Usage Count -->
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.usageCount' | transloco }}</span>
              <span class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ job.usage_count || 0 }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tags -->
        @if (job.tags && job.tags.length > 0) {
          <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {{ 'jobDetailsDialog.sections.tags' | transloco }}
            </h3>
            <div class="flex flex-wrap gap-2">
              @for (tag of job.tags; track $index) {
                <span
                  class="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300"
                >
                  <i class="fa-light fa-tag me-1"></i>
                  {{ tag }}
                </span>
              }
            </div>
          </div>
        }
      </div>

      <!-- Right Column -->
      <div class="space-y-6">
        <!-- Instances -->
        <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ 'jobDetailsDialog.sections.instances' | transloco }}
            @if (job.instances) {
              <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
                ({{ job.instances.length }})
              </span>
            }
          </h3>
          @if (job.instances && job.instances.length > 0) {
            <div class="space-y-3 max-h-96 overflow-y-auto">
              @for (instance of job.instances; track instance.uuid) {
                <div class="bg-white dark:bg-neutral-800 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
                  <div class="flex items-center justify-between mb-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
                      {{ instance.name || ('jobDetailsDialog.labels.unnamedInstance' | transloco) }}
                    </h4>
                    <span
                      [class]="getStatusColor(instance.status)"
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                    >
                      <i [class]="getStatusIcon(instance.status)"></i>
                      {{ getInstanceStatusLabel(instance.status) }}
                    </span>
                  </div>
                  @if (instance.uuid) {
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {{ 'jobDetailsDialog.labels.instanceId' | transloco }}: <span class="font-mono">{{ instance.uuid }}</span>
                    </div>
                  }
                  @if (instance.created_at) {
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ 'jobDetailsDialog.labels.created' | transloco }}: {{ instance.created_at | date: 'short' }}
                    </div>
                  }
                  @if (instance.parameters && instance.parameters.length > 0) {
                    <div class="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
                      <div class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {{ 'jobDetailsDialog.labels.parameters' | transloco }}
                      </div>
                      <div class="space-y-1">
                        @for (param of instance.parameters; track param.uuid) {
                          <div class="text-xs text-gray-600 dark:text-gray-400">
                            <span class="font-medium">{{ getInstanceParameterLabel(param) }}:</span>
                            <span class="ms-1">{{ getInstanceParameterValue(param) }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="text-center py-8 text-gray-500 dark:text-gray-400">
              <i class="fa-light fa-inbox text-3xl mb-2"></i>
              <p class="text-sm">{{ 'jobDetailsDialog.empty.noInstances' | transloco }}</p>
            </div>
          }
        </div>

        <!-- Additional Information -->
        @if (job.workflow_id || job.dify_app_id) {
          <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {{ 'jobDetailsDialog.sections.additionalInfo' | transloco }}
            </h3>
            <div class="space-y-3">
              @if (job.workflow_id) {
                <div class="flex justify-between items-center">
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.workflowId' | transloco }}</span>
                  <span class="text-sm text-gray-900 dark:text-white font-mono">{{ job.workflow_id }}</span>
                </div>
              }
              @if (job.dify_app_id) {
                <div class="flex justify-between items-center">
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'jobDetailsDialog.labels.difyAppId' | transloco }}</span>
                  <span class="text-sm text-gray-900 dark:text-white font-mono">{{ job.dify_app_id }}</span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  } @else {
    <div class="text-center py-12">
      <p class="text-gray-500 dark:text-gray-400">{{ 'jobDetailsDialog.empty.jobNotFound' | transloco }}</p>
    </div>
  }
</div>

